import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Mail, KeyRound, User as UserIcon, Image as ImageIcon,
  Shield, Trash2, Upload, AtSign,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/use-auth";
import { updateChefSlug, deleteMyAccount } from "@/lib/account.functions";

export const Route = createFileRoute("/_authenticated/dashboard/account")({
  head: () => ({ meta: [{ title: "Impostazioni Account — ChefConnect" }] }),
  component: AccountSettingsPage,
});

function slugify(s: string) {
  return s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").slice(0, 60);
}

function AccountSettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Provider di login (email / google / ...)
  const provider = (user?.app_metadata?.provider as string | undefined) ?? "email";
  const isOAuth = provider !== "email";

  // Profilo: full_name + avatar_url (storage path) + chef slug se presente
  const profileQ = useQuery({
    enabled: !!user,
    queryKey: ["my-profile", user?.id],
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("*").eq("id", user!.id).maybeSingle();
      return data;
    },
  });
  const chefQ = useQuery({
    enabled: !!user,
    queryKey: ["my-chef-slug", user?.id],
    queryFn: async () => {
      const { data } = await supabase.from("chefs").select("slug, display_name").eq("user_id", user!.id).maybeSingle();
      return data;
    },
  });

  // Avatar signed URL
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    (async () => {
      const path = profileQ.data?.avatar_url;
      if (!path) { if (active) setAvatarUrl(null); return; }
      // Se è già un URL assoluto (es. avatar Google OAuth) usalo direttamente,
      // altrimenti firma il path nello storage.
      if (/^https?:\/\//i.test(path)) {
        if (active) setAvatarUrl(path);
        return;
      }
      const { data } = await supabase.storage.from("avatars").createSignedUrl(path, 3600);
      if (active) setAvatarUrl(data?.signedUrl ?? null);
    })();
    return () => { active = false; };
  }, [profileQ.data?.avatar_url]);

  // ───── 1. Informazioni personali ─────
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [slug, setSlug] = useState("");
  useEffect(() => {
    const full = (profileQ.data?.full_name ?? "").trim();
    const parts = full.split(/\s+/);
    setFirstName(parts[0] ?? "");
    setLastName(parts.slice(1).join(" "));
  }, [profileQ.data?.full_name]);
  useEffect(() => { setSlug(chefQ.data?.slug ?? ""); }, [chefQ.data?.slug]);

  const updateSlugFn = useServerFn(updateChefSlug);
  const savePersonalMut = useMutation({
    mutationFn: async () => {
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      const { error } = await supabase.from("profiles")
        .update({ full_name: fullName || null }).eq("id", user!.id);
      if (error) throw error;
      // slug — solo se chef e cambiato
      if (chefQ.data && slug && slug !== chefQ.data.slug) {
        await updateSlugFn({ data: { slug } });
      }
    },
    onSuccess: () => {
      toast.success("Informazioni aggiornate");
      qc.invalidateQueries({ queryKey: ["my-profile", user?.id] });
      qc.invalidateQueries({ queryKey: ["my-chef-slug", user?.id] });
      qc.invalidateQueries({ queryKey: ["my-chef", user?.id] });
    },
    onError: (e: Error) => toast.error(e.message || "Errore"),
  });

  // ───── 2. Email ─────
  const [newEmail, setNewEmail] = useState("");
  const changeEmailMut = useMutation({
    mutationFn: async () => {
      if (!newEmail.includes("@")) throw new Error("Email non valida");
      const { error } = await supabase.auth.updateUser(
        { email: newEmail.trim() },
        { emailRedirectTo: window.location.origin + "/dashboard/account" },
      );
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Ti abbiamo inviato un'email di conferma al nuovo indirizzo");
      setNewEmail("");
    },
    onError: (e: Error) => {
      const msg = e.message.toLowerCase();
      if (msg.includes("already") || msg.includes("registered")) toast.error("Email già in uso");
      else toast.error(e.message || "Errore nel cambio email");
    },
  });

  // ───── 3. Password ─────
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const changePwdMut = useMutation({
    mutationFn: async () => {
      if (pwd.next.length < 8) throw new Error("La nuova password deve avere almeno 8 caratteri");
      if (pwd.next !== pwd.confirm) throw new Error("Le password non coincidono");
      // Riautentica con la password attuale
      const { error: signErr } = await supabase.auth.signInWithPassword({
        email: user!.email!, password: pwd.current,
      });
      if (signErr) throw new Error("Password attuale errata");
      const { error } = await supabase.auth.updateUser({ password: pwd.next });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Password aggiornata");
      setPwd({ current: "", next: "", confirm: "" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // ───── 4. Avatar ─────
  const fileRef = useRef<HTMLInputElement>(null);
  const uploadAvatarMut = useMutation({
    mutationFn: async (file: File) => {
      if (!file.type.startsWith("image/")) throw new Error("Carica un'immagine");
      if (file.size > 5 * 1024 * 1024) throw new Error("Massimo 5 MB");
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const path = `${user!.id}/avatar-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("avatars")
        .upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;
      // Rimuovi il vecchio
      if (profileQ.data?.avatar_url && profileQ.data.avatar_url !== path) {
        await supabase.storage.from("avatars").remove([profileQ.data.avatar_url]).catch(() => {});
      }
      const { error } = await supabase.from("profiles")
        .update({ avatar_url: path }).eq("id", user!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Avatar aggiornato");
      qc.invalidateQueries({ queryKey: ["my-profile", user?.id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const removeAvatarMut = useMutation({
    mutationFn: async () => {
      if (profileQ.data?.avatar_url) {
        await supabase.storage.from("avatars").remove([profileQ.data.avatar_url]).catch(() => {});
      }
      const { error } = await supabase.from("profiles")
        .update({ avatar_url: null }).eq("id", user!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Avatar rimosso");
      qc.invalidateQueries({ queryKey: ["my-profile", user?.id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // ───── 6. Eliminazione account ─────
  const [delText, setDelText] = useState("");
  const deleteFn = useServerFn(deleteMyAccount);
  const deleteMut = useMutation({
    mutationFn: () => deleteFn({ data: { confirmation: "ELIMINA" as const } }),
    onSuccess: async () => {
      toast.success("Account eliminato");
      await qc.cancelQueries();
      qc.clear();
      await supabase.auth.signOut();
      navigate({ to: "/", replace: true });
    },
    onError: (e: Error) => toast.error(e.message || "Errore nell'eliminazione"),
  });

  const initial = (profileQ.data?.full_name ?? user?.email ?? "?").charAt(0).toUpperCase();

  return (
    <div className="container mx-auto max-w-3xl px-4 py-10">
      <Link to="/dashboard" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="mr-1 h-4 w-4" /> Torna alla Dashboard
      </Link>
      <h1 className="mt-3 font-display text-4xl">Impostazioni Account</h1>
      <p className="mt-1 text-sm text-muted-foreground">Gestisci profilo, sicurezza e preferenze.</p>

      {/* 1. Informazioni personali */}
      <section className="mt-8 rounded-xl border border-border bg-card p-6">
        <h2 className="flex items-center gap-2 font-display text-2xl"><UserIcon className="h-5 w-5" /> Informazioni personali</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="firstName">Nome</Label>
            <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={50} />
          </div>
          <div>
            <Label htmlFor="lastName">Cognome</Label>
            <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} maxLength={50} />
          </div>
          {chefQ.data && (
            <div className="sm:col-span-2">
              <Label htmlFor="slug" className="inline-flex items-center gap-1"><AtSign className="h-3.5 w-3.5" /> Username pubblico</Label>
              <Input id="slug" value={slug} onChange={(e) => setSlug(slugify(e.target.value))}
                pattern="[a-z0-9-]+" maxLength={60} />
              <p className="mt-1 text-xs text-muted-foreground">
                Il tuo profilo sarà raggiungibile su <code className="text-foreground">/chef/{slug || "..."}</code>
              </p>
            </div>
          )}
        </div>
        <Button className="mt-4 bg-gold text-gold-foreground hover:bg-gold/90"
          disabled={savePersonalMut.isPending} onClick={() => savePersonalMut.mutate()}>
          {savePersonalMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Salva modifiche
        </Button>
      </section>

      {/* 4. Avatar */}
      <section className="mt-6 rounded-xl border border-border bg-card p-6">
        <h2 className="flex items-center gap-2 font-display text-2xl"><ImageIcon className="h-5 w-5" /> Avatar</h2>
        <div className="mt-4 flex items-center gap-5">
          <div className="grid h-20 w-20 place-items-center overflow-hidden rounded-full border bg-muted text-2xl font-display">
            {avatarUrl ? <img src={avatarUrl} alt="Avatar" className="h-full w-full object-cover" /> : <span>{initial}</span>}
          </div>
          <div className="flex flex-wrap gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAvatarMut.mutate(f); e.target.value = ""; }} />
            <Button variant="outline" disabled={uploadAvatarMut.isPending} onClick={() => fileRef.current?.click()}>
              {uploadAvatarMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              {avatarUrl ? "Sostituisci" : "Carica"}
            </Button>
            {avatarUrl && (
              <Button variant="ghost" disabled={removeAvatarMut.isPending} onClick={() => removeAvatarMut.mutate()}>
                {removeAvatarMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                Rimuovi
              </Button>
            )}
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">JPG, PNG o WebP. Massimo 5 MB.</p>
      </section>

      {/* 2. Email */}
      <section className="mt-6 rounded-xl border border-border bg-card p-6">
        <h2 className="flex items-center gap-2 font-display text-2xl"><Mail className="h-5 w-5" /> Email</h2>
        <p className="mt-2 text-sm text-muted-foreground">Email attuale: <strong className="text-foreground">{user?.email}</strong></p>
        {isOAuth ? (
          <p className="mt-3 rounded-lg border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
            Hai effettuato l'accesso con {provider}. Per cambiare email modificala dal tuo account {provider}.
          </p>
        ) : (
          <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto]">
            <Input type="email" placeholder="nuova@email.it" value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)} />
            <Button variant="outline" disabled={changeEmailMut.isPending || !newEmail}
              onClick={() => changeEmailMut.mutate()}>
              {changeEmailMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Cambia email
            </Button>
          </div>
        )}
      </section>

      {/* 3. Password */}
      {!isOAuth && (
        <section className="mt-6 rounded-xl border border-border bg-card p-6">
          <h2 className="flex items-center gap-2 font-display text-2xl"><KeyRound className="h-5 w-5" /> Password</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <div>
              <Label htmlFor="cur">Attuale</Label>
              <Input id="cur" type="password" value={pwd.current} onChange={(e) => setPwd((p) => ({ ...p, current: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="new">Nuova</Label>
              <Input id="new" type="password" value={pwd.next} onChange={(e) => setPwd((p) => ({ ...p, next: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="conf">Conferma</Label>
              <Input id="conf" type="password" value={pwd.confirm} onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))} />
            </div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Almeno 8 caratteri.</p>
          <Button className="mt-4" variant="outline"
            disabled={changePwdMut.isPending || !pwd.current || !pwd.next || !pwd.confirm}
            onClick={() => changePwdMut.mutate()}>
            {changePwdMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Aggiorna password
          </Button>
        </section>
      )}

      {/* 5. Sicurezza */}
      <section className="mt-6 rounded-xl border border-border bg-card p-6">
        <h2 className="flex items-center gap-2 font-display text-2xl"><Shield className="h-5 w-5" /> Sicurezza</h2>
        <div className="mt-3 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Metodo di accesso</span>
          <span className="font-medium capitalize">{provider === "email" ? "Email e password" : provider}</span>
        </div>
        {isOAuth && (
          <p className="mt-2 text-xs text-muted-foreground">
            Accesso gestito da {provider}. Il cambio password si effettua dal tuo account {provider}.
          </p>
        )}
      </section>

      {/* 6. Eliminazione account */}
      <section className="mt-6 rounded-xl border border-destructive/40 bg-destructive/5 p-6">
        <h2 className="flex items-center gap-2 font-display text-2xl text-destructive">
          <Trash2 className="h-5 w-5" /> Elimina account
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Rimuoverà definitivamente il tuo profilo, le foto, gli annunci e ogni dato associato. <strong className="text-foreground">L'operazione è irreversibile.</strong>
        </p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" className="mt-4">Elimina il mio account</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Sei sicuro?</AlertDialogTitle>
              <AlertDialogDescription>
                Per confermare, scrivi <strong>ELIMINA</strong> qui sotto. L'azione non può essere annullata.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Separator />
            <Input autoFocus value={delText} onChange={(e) => setDelText(e.target.value)} placeholder="Scrivi ELIMINA" />
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDelText("")}>Annulla</AlertDialogCancel>
              <AlertDialogAction
                disabled={delText !== "ELIMINA" || deleteMut.isPending}
                onClick={(e) => { e.preventDefault(); deleteMut.mutate(); }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Elimina definitivamente
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </section>
    </div>
  );
}